/**
 * 記事コンテンツ用のスタイル定義
 * プレビュー画面と記事詳細ページで使用
 */

export interface ArticleStyles {
  h1: {
    fontSize: string;
    color: string;
    fontFamily: string;
    fontWeight: string;
    lineHeight: string;
    letterSpacing: string;
    marginTop: string;
    marginBottom: string;
    paddingBottom: string;
  };
  h2: {
    fontSize: string;
    color: string;
    fontFamily: string;
    fontWeight: string;
    lineHeight: string;
    letterSpacing: string;
    marginTop: string;
    marginBottom: string;
    padding: string;
    background: string;
    borderRadius: string;
  };
  h3: {
    fontSize: string;
    color: string;
    fontFamily: string;
    fontWeight: string;
    lineHeight: string;
    letterSpacing: string;
    marginTop: string;
    marginBottom: string;
    paddingBottom: string;
  };
  h4: {
    fontSize: string;
    color: string;
    marginTop: string;
    marginBottom: string;
    fontWeight: string;
  };
  p: {
    fontSize: string;
    color: string;
    fontFamily: string;
    fontWeight: string;
    lineHeight: string;
    marginBottom: string;
  };
  a: {
    color: string;
    textDecoration: string;
    hoverColor: string;
  };
  strong: {
    color: string;
    fontWeight: string;
  };
  ul: {
    marginLeft: string;
    listStyle: string;
    lineHeight: string;
  };
  ol: {
    marginTop: string;
    marginBottom: string;
    paddingLeft: string;
  };
  li: {
    fontSize: string;
    color: string;
    lineHeight: string;
    marginBottom: string;
  };
  img: {
    maxWidth: string;
    height: string;
    borderRadius: string;
    boxShadow: string;
    marginBottom: string;
  };
  blockquote: {
    borderLeft: string;
    paddingLeft: string;
    backgroundColor: string;
    fontSize: string;
    color: string;
    fontStyle: string;
    marginTop: string;
    marginBottom: string;
  };
  code: {
    backgroundColor: string;
    color: string;
    fontFamily: string;
    fontSize: string;
    padding: string;
    borderRadius: string;
  };
  pre: {
    backgroundColor: string;
    color: string;
    fontFamily: string;
    fontSize: string;
    padding: string;
    borderRadius: string;
    overflow: string;
    marginTop: string;
    marginBottom: string;
  };
}

export const defaultArticleStyles: ArticleStyles = {
  h1: {
    fontSize: '2.25rem',
    color: 'linear-gradient(135deg, #02D0F0 0%, #07DBCA 100%)',
    fontFamily: 'Noto Sans JP, Inter, sans-serif',
    fontWeight: '700',
    lineHeight: '1.4',
    letterSpacing: '0.5px',
    marginTop: '2.5rem',
    marginBottom: '1.5rem',
    paddingBottom: '1rem',
  },
  h2: {
    fontSize: '1.875rem',
    color: '#111827',
    fontFamily: 'Noto Sans JP, Inter, sans-serif',
    fontWeight: '700',
    lineHeight: '1.4',
    letterSpacing: '0.5px',
    marginTop: '2rem',
    marginBottom: '1.25rem',
    padding: '1rem 1.5rem',
    background: 'linear-gradient(90deg, rgba(2, 208, 240, 0.08) 0%, rgba(7, 219, 202, 0.05) 100%)',
    borderRadius: '0 8px 8px 0',
  },
  h3: {
    fontSize: '1.5rem',
    color: '#111827',
    fontFamily: 'Noto Sans JP, Inter, sans-serif',
    fontWeight: '700',
    lineHeight: '1.4',
    letterSpacing: '0.5px',
    marginTop: '1.5rem',
    marginBottom: '1rem',
    paddingBottom: '0.75rem',
  },
  h4: {
    fontSize: '1.25rem',
    color: '#111827',
    marginTop: '1.25rem',
    marginBottom: '0.75rem',
    fontWeight: '700',
  },
  p: {
    fontSize: '1rem',
    color: '#111827',
    fontFamily: 'Noto Sans JP, sans-serif',
    fontWeight: '400',
    lineHeight: '1.6',
    marginBottom: '1rem',
  },
  a: {
    color: '#02D0F0',
    textDecoration: 'none',
    hoverColor: '#07DBCA',
  },
  strong: {
    color: '#111827',
    fontWeight: '700',
  },
  ul: {
    marginLeft: '1.5rem',
    listStyle: 'disc',
    lineHeight: '1.6',
  },
  ol: {
    marginTop: '1rem',
    marginBottom: '1rem',
    paddingLeft: '1.5rem',
  },
  li: {
    fontSize: '1rem',
    color: '#111827',
    lineHeight: '1.6',
    marginBottom: '0.5rem',
  },
  img: {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    marginBottom: '1.5rem',
  },
  blockquote: {
    borderLeft: '4px solid #02D0F0',
    paddingLeft: '1rem',
    backgroundColor: '#F6F8FA',
    fontSize: '1rem',
    color: '#6B7280',
    fontStyle: 'italic',
    marginTop: '1rem',
    marginBottom: '1rem',
  },
  code: {
    backgroundColor: '#F6F8FA',
    color: '#111827',
    fontFamily: "Monaco, 'Courier New', monospace",
    fontSize: '0.875rem',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
  },
  pre: {
    backgroundColor: '#F6F8FA',
    color: '#111827',
    fontFamily: "Monaco, 'Courier New', monospace",
    fontSize: '0.875rem',
    padding: '1rem',
    borderRadius: '8px',
    overflow: 'auto',
    marginTop: '1rem',
    marginBottom: '1rem',
  },
};

/**
 * スタイルオブジェクトをCSS文字列に変換
 */
export const stylesToCSS = (styles: ArticleStyles): string => {
  return `
    .article-content h1 {
      font-size: ${styles.h1.fontSize};
      background: ${styles.h1.color};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      font-family: ${styles.h1.fontFamily};
      font-weight: ${styles.h1.fontWeight};
      line-height: ${styles.h1.lineHeight};
      letter-spacing: ${styles.h1.letterSpacing};
      margin-top: ${styles.h1.marginTop};
      margin-bottom: ${styles.h1.marginBottom};
      padding-bottom: ${styles.h1.paddingBottom};
      position: relative;
    }
    .article-content h1::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 80px;
      height: 4px;
      background: linear-gradient(90deg, #02D0F0 0%, #07DBCA 100%);
    }
    .article-content h2 {
      font-size: ${styles.h2.fontSize};
      color: ${styles.h2.color};
      font-family: ${styles.h2.fontFamily};
      font-weight: ${styles.h2.fontWeight};
      line-height: ${styles.h2.lineHeight};
      letter-spacing: ${styles.h2.letterSpacing};
      margin-top: ${styles.h2.marginTop};
      margin-bottom: ${styles.h2.marginBottom};
      padding: ${styles.h2.padding};
      background: ${styles.h2.background};
      border-radius: ${styles.h2.borderRadius};
      position: relative;
    }
    .article-content h2::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: linear-gradient(180deg, #02D0F0 0%, #07DBCA 100%);
      border-radius: 8px 0 0 8px;
    }
    .article-content h3 {
      font-size: ${styles.h3.fontSize};
      color: ${styles.h3.color};
      font-family: ${styles.h3.fontFamily};
      font-weight: ${styles.h3.fontWeight};
      line-height: ${styles.h3.lineHeight};
      letter-spacing: ${styles.h3.letterSpacing};
      margin-top: ${styles.h3.marginTop};
      margin-bottom: ${styles.h3.marginBottom};
      padding-bottom: ${styles.h3.paddingBottom};
      position: relative;
    }
    .article-content h3::before {
      content: '◆';
      color: #02D0F0;
      margin-right: 0.5rem;
    }
    .article-content h3::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg, #02D0F0 0%, transparent 100%);
    }
    .article-content h4 {
      font-size: ${styles.h4.fontSize};
      color: ${styles.h4.color};
      margin-top: ${styles.h4.marginTop};
      margin-bottom: ${styles.h4.marginBottom};
      font-weight: ${styles.h4.fontWeight};
    }
    .article-content p {
      font-size: ${styles.p.fontSize};
      color: ${styles.p.color};
      font-family: ${styles.p.fontFamily};
      font-weight: ${styles.p.fontWeight};
      line-height: ${styles.p.lineHeight};
      margin-bottom: ${styles.p.marginBottom};
    }
    .article-content a {
      color: ${styles.a.color};
      text-decoration: ${styles.a.textDecoration};
    }
    .article-content a:hover {
      color: ${styles.a.hoverColor};
      text-decoration: underline;
    }
    .article-content strong {
      color: ${styles.strong.color};
      font-weight: ${styles.strong.fontWeight};
    }
    .article-content ul {
      margin-left: ${styles.ul.marginLeft};
      list-style-type: ${styles.ul.listStyle};
      line-height: ${styles.ul.lineHeight};
    }
    .article-content ol {
      margin-top: ${styles.ol.marginTop};
      margin-bottom: ${styles.ol.marginBottom};
      padding-left: ${styles.ol.paddingLeft};
      list-style-type: decimal;
    }
    .article-content li {
      font-size: ${styles.li.fontSize};
      color: ${styles.li.color};
      line-height: ${styles.li.lineHeight};
      margin-bottom: ${styles.li.marginBottom};
    }
    .article-content img {
      max-width: ${styles.img.maxWidth};
      height: ${styles.img.height};
      border-radius: ${styles.img.borderRadius};
      box-shadow: ${styles.img.boxShadow};
      margin-bottom: ${styles.img.marginBottom};
    }
    .article-content blockquote {
      border-left: ${styles.blockquote.borderLeft};
      padding-left: ${styles.blockquote.paddingLeft};
      background-color: ${styles.blockquote.backgroundColor};
      font-size: ${styles.blockquote.fontSize};
      color: ${styles.blockquote.color};
      font-style: ${styles.blockquote.fontStyle};
      margin-top: ${styles.blockquote.marginTop};
      margin-bottom: ${styles.blockquote.marginBottom};
    }
    .article-content code {
      background-color: ${styles.code.backgroundColor};
      color: ${styles.code.color};
      font-family: ${styles.code.fontFamily};
      font-size: ${styles.code.fontSize};
      padding: ${styles.code.padding};
      border-radius: ${styles.code.borderRadius};
    }
    .article-content pre {
      background-color: ${styles.pre.backgroundColor};
      color: ${styles.pre.color};
      font-family: ${styles.pre.fontFamily};
      font-size: ${styles.pre.fontSize};
      padding: ${styles.pre.padding};
      border-radius: ${styles.pre.borderRadius};
      overflow: ${styles.pre.overflow};
      margin-top: ${styles.pre.marginTop};
      margin-bottom: ${styles.pre.marginBottom};
    }
    .article-content pre code {
      background-color: transparent;
      color: inherit;
      padding: 0;
    }
    /* 目次（旧 nav.toc 埋め込みスタイル + SOCIAL TRIP トーン） */
    .article-content nav.article-toc {
      background:
        linear-gradient(180deg, rgba(2, 208, 240, 0.08) 0%, rgba(7, 219, 202, 0.04) 18%, #fafafa 56%);
      border: 1px solid #dfe9ee;
      border-radius: 12px;
      padding: 14px 16px;
      margin: 18px 0 26px;
      box-shadow: 0 8px 20px rgba(2, 208, 240, 0.08);
    }
    .article-content nav.article-toc > h2.article-toc__title {
      font-size: 1.05rem;
      font-weight: 700;
      color: #111827;
      margin: 0 0 12px;
      padding: 0;
      background: none;
      border-radius: 0;
      position: static;
      line-height: 1.4;
      letter-spacing: normal;
      display: flex;
      align-items: center;
      gap: 0.45rem;
    }
    .article-content nav.article-toc > h2.article-toc__title::before {
      content: '';
      display: inline-block;
      width: 10px;
      height: 10px;
      border-radius: 9999px;
      background: linear-gradient(135deg, #02D0F0 0%, #07DBCA 100%);
      box-shadow: 0 0 0 3px rgba(2, 208, 240, 0.15);
    }
    .article-content nav.article-toc > h2.article-toc__title::after {
      content: none;
      display: none;
    }
    .article-content nav.article-toc ol.article-toc__list {
      margin: 0;
      padding-left: 1.35rem;
      list-style-type: decimal;
    }
    .article-content nav.article-toc li.article-toc__item {
      font-size: 0.95rem;
      color: #374151;
      line-height: 1.65;
      margin-bottom: 0.4rem;
    }
    .article-content nav.article-toc a.article-toc__link {
      color: #0b6cff;
      text-decoration: none;
      transition: color 0.2s ease;
    }
    .article-content nav.article-toc a.article-toc__link--h2 {
      font-weight: 600;
    }
    .article-content nav.article-toc a.article-toc__link:hover {
      color: #094fcc;
      text-decoration: underline;
    }
    @media (max-width: 640px) {
      .article-content nav.article-toc {
        padding: 12px 14px;
      }
      .article-content nav.article-toc li.article-toc__item {
        line-height: 1.6;
      }
    }
  `;
};

/**
 * LocalStorageからカスタムスタイルを取得
 */
export const getCustomStyles = (): ArticleStyles | null => {
  try {
    const saved = localStorage.getItem('customArticleStyles');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('カスタムスタイルの読み込みエラー:', error);
  }
  return null;
};

/**
 * スタイルを適用するためのCSS文字列を取得
 */
export const getArticleStylesCSS = (): string => {
  const customStyles = getCustomStyles();
  return stylesToCSS(customStyles || defaultArticleStyles);
};

/**
 * 後方互換性のため - クラス名を返す（ダミー）
 */
export const getArticleContentClassName = (): string => {
  return 'article-content';
};
